import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Container,
  Header,
  SpaceBetween,
  Button,
  Box,
  Alert,
  StatusIndicator,
  Spinner,
  Icon,
} from "@cloudscape-design/components";
import axios from "axios";
import { useAuth } from "../AuthContext";

/**
 * AgentTestWidget — isolated iframe-based widget for testing an agent.
 * The widget script runs inside an iframe so its CSS/JS cannot impact
 * the main application, and the app's Cloudscape styles take priority.
 */
const AgentTestWidget = ({ agent }) => {
  const { user } = useAuth();
  const [widgetCode, setWidgetCode] = useState(null);
  const [widgetLoading, setWidgetLoading] = useState(false);
  const [widgetError, setWidgetError] = useState(null);
  const [testActive, setTestActive] = useState(false);
  const iframeRef = useRef(null);
  const widgetInstanceRef = useRef(null);

  const isVoiceAgent = agent && (agent.ai_agent_type === "voice" || !agent.ai_agent_type);
  const isTextAgent = agent && (agent.ai_agent_type === "text" || agent.ai_agent_type === "text_only");

  const fetchWidgetCode = useCallback(async () => {
    if (!agent) return;
    const agentId = agent.id || agent.adexcloud_agent_id;
    if (!agentId) return;
    setWidgetLoading(true);
    setWidgetError(null);
    try {
      const accessToken = user?.access_token || user?.token || localStorage.getItem("access_token");
      const res = await axios.get(`/api/agents/${agentId}/widget-code/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (res.data?.widget_code) {
        setWidgetCode(res.data.widget_code);
      } else {
        setWidgetError("No widget code available for this agent.");
      }
    } catch (err) {
      console.error("Error fetching widget code:", err);
      setWidgetError(err.response?.data?.message || "Failed to fetch widget code.");
    } finally {
      setWidgetLoading(false);
    }
  }, [agent, user]);

  useEffect(() => {
    if (agent) {
      fetchWidgetCode();
    }
  }, [agent, fetchWidgetCode]);

  // Build the iframe document that loads the widget in isolation
  const buildIframeDoc = useCallback(() => {
    if (!widgetCode) return "";

    // Extract the script src URL from the widget code
    const srcMatch = widgetCode.match(/<script\s+src=["']([^"']+)["']/);
    const widgetSrc = srcMatch ? srcMatch[1] : "/adexgenie-widget.min.js";

    // Extract the init script (the second <script> block)
    const initMatch = widgetCode.match(/<script>([\s\S]*?)<\/script>/);
    const initScript = initMatch ? initMatch[1] : "";

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    html, body { height: 100%; width: 100%; overflow: hidden; background: #fff; font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }

    /* Override widget fixed positioning to be relative inside iframe */
    #adexgenie-widget { position: relative !important; width: 100% !important; height: 100% !important; }
    .adexgenie-launcher { display: none !important; }
    .adexgenie-window {
      display: flex !important;
      position: relative !important;
      bottom: auto !important;
      right: auto !important;
      width: 100% !important;
      height: 100% !important;
      border: none !important;
      border-radius: 0 !important;
      box-shadow: none !important;
    }
    .adexgenie-header { border-radius: 0 !important; }
  </style>
</head>
<body>
  <div id="widget-container" style="width:100%;height:100%;"></div>
  <script src="${widgetSrc}"></script>
  <script>
    (function() {
      ${initScript}

      // Auto-open the widget after initialization
      function tryOpen() {
        var win = document.getElementById('adexgenie-window');
        var launcher = document.getElementById('adexgenie-launcher');
        if (win) {
          win.style.display = 'flex';
          if (launcher) launcher.style.display = 'none';
        } else {
          setTimeout(tryOpen, 200);
        }
      }
      // Wait for widget to render then auto-open
      setTimeout(tryOpen, 500);
    })();
  </script>
</body>
</html>`;
  }, [widgetCode]);

  const handleStartTest = useCallback(() => {
    setTestActive(true);
  }, []);

  const handleEndTest = useCallback(() => {
    setTestActive(false);
    // Clear the iframe src to destroy the widget session
    if (iframeRef.current) {
      iframeRef.current.srcdoc = "";
    }
  }, []);

  // Cleanup on unmount or agent change
  useEffect(() => {
    return () => {
      setTestActive(false);
      setWidgetCode(null);
    };
  }, [agent]);

  if (!agent) return null;

  return (
    <Container
      header={
        <Header
          variant="h4"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              {testActive ? (
                <Button variant="critical" iconName="close" onClick={handleEndTest}>
                  End Chat
                </Button>
              ) : (
                <Button
                  variant="primary"
                  iconName={isVoiceAgent ? "call" : "chat"}
                  onClick={handleStartTest}
                  disabled={widgetLoading || !!widgetError}
                >
                  {isVoiceAgent ? "Start Test Call" : "Start Test Chat"}
                </Button>
              )}
            </SpaceBetween>
          }
        >
          <Icon name={isVoiceAgent ? "call" : "chat"} size="small" /> Test {isVoiceAgent ? "Call" : "Chat"}
        </Header>
      }
    >
      <SpaceBetween size="m">
        {widgetLoading && <Spinner size="normal" />}

        {widgetError && (
          <Alert type="error" header="Widget Error">
            {widgetError}
          </Alert>
        )}

        {!testActive && !widgetLoading && !widgetError && (
          <Box variant="p" color="text-body-secondary" textAlign="center" padding="l">
            Click <strong>{isVoiceAgent ? "Start Test Call" : "Start Test Chat"}</strong> to test your agent in an isolated widget.
            The session will open in a secure sandbox below.
          </Box>
        )}

        {testActive && widgetCode && (
          <div
            style={{
              width: "100%",
              height: "500px",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <iframe
              ref={iframeRef}
              title="Agent Test Widget"
              srcDoc={buildIframeDoc()}
              sandbox="allow-scripts allow-same-origin allow-forms"
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                display: "block",
              }}
            />
          </div>
        )}

        {testActive && !widgetCode && (
          <Alert type="warning" header="No widget available">
            The widget code for this agent is not available. Make sure the agent is deployed.
          </Alert>
        )}
      </SpaceBetween>
    </Container>
  );
};

export default AgentTestWidget;
